import axiosInstance from '../../api/axios';
import { API_ENDPOINTS } from '../../api/endpoints';

/**
 * `id` = UUID for mutation paths; `cycle_id` = business key for GET /processes/{cycle_id} etc.
 */
function normalizeSoxCycleRecord(c) {
  if (!c || typeof c !== 'object') return c;
  const out = { ...c };
  if (c.id != null) out.id = c.id;
  else if (c.cycle_id != null) out.id = c.cycle_id;
  if (c.cycle_id != null) out.cycle_id = c.cycle_id;
  else if (c.id != null) out.cycle_id = c.id;
  return out;
}

/**
 * `id` = UUID for mutation paths; `process_id` = business key for non-mutation APIs / URLs.
 */
function normalizeSoxProcessRecord(p) {
  if (!p || typeof p !== 'object') return p;
  const out = { ...p };
  if (p.id != null) out.id = p.id;
  else if (p.process_id != null) out.id = p.process_id;
  if (p.process_id != null) out.process_id = p.process_id;
  else if (p.id != null) out.process_id = p.id;
  return out;
}

export async function fetchSoxCycles(auditYearId) {
  const params = auditYearId ? { audit_year_id: auditYearId } : {};
  const response = await axiosInstance.get(API_ENDPOINTS.listSoxCycles, { params });
  if (Array.isArray(response.data?.cycles)) {
    return response.data.cycles.map(normalizeSoxCycleRecord);
  }
  return [];
}

export async function fetchProcessesForCycle(cycleListKey, auditYearId) {
  const pageSize = 20;
  const all = [];
  let page = 1;

  while (true) {
    const params = { page, page_size: pageSize };
    if (auditYearId) params.audit_year_id = auditYearId;
    const response = await axiosInstance.get(API_ENDPOINTS.getProcesses(cycleListKey), { params });
    if (response.data?.success !== true) break;

    const batch = Array.isArray(response.data.processes)
      ? response.data.processes.map(normalizeSoxProcessRecord)
      : [];
    all.push(...batch);

    const totalPages = Number(response.data?.pagination?.total_pages) || 1;
    if (page >= totalPages || batch.length === 0) break;
    page += 1;
  }

  return all;
}

/** Persist process sequence for a cycle. processUuids must be processes.id in desired order. */
export async function reorderCycleProcessesSequence(cycleListKey, processUuids, auditYearId) {
  const params = {};
  if (auditYearId) params.audit_year_id = auditYearId;
  const response = await axiosInstance.put(
    API_ENDPOINTS.reorderCycleProcessesSequence(cycleListKey),
    { process_ids: processUuids },
    { params }
  );
  if (response?.data?.success !== true) {
    throw new Error(response?.data?.error || 'Failed to update process sequence');
  }
  return response.data;
}

export async function putSoxCycleStatus(cycleUuid, status) {
  const res = await axiosInstance.put(API_ENDPOINTS.putSoxCycleStatus(cycleUuid), { status });
  return res.data;
}

export async function putSoxCycleRiskScore(cycleUuid, riskScore) {
  const res = await axiosInstance.put(API_ENDPOINTS.putSoxCycleRiskScore(cycleUuid), {
    riskScore
  });
  return res.data;
}

export async function patchSoxCycleActiveStatus(cycleUuid, is_active) {
  const res = await axiosInstance.patch(API_ENDPOINTS.patchSoxCycleToggleActive(cycleUuid), {
    is_active
  });
  return res.data;
}

export async function patchSoxCycleArchive(cycleUuid) {
  const res = await axiosInstance.patch(API_ENDPOINTS.patchSoxCycleArchive(cycleUuid));
  return res.data;
}

export async function patchSoxCycleUnarchive(cycleUuid) {
  const res = await axiosInstance.patch(API_ENDPOINTS.patchSoxCycleUnarchive(cycleUuid));
  return res.data;
}

export async function putProcessStatus(processUuid, status) {
  const res = await axiosInstance.put(API_ENDPOINTS.putProcessStatus(processUuid), { status });
  return res.data;
}

export async function patchProcessActiveStatus(processUuid, is_active) {
  const res = await axiosInstance.patch(API_ENDPOINTS.patchProcessToggleActive(processUuid), {
    is_active
  });
  return res.data;
}

export async function deleteProcess(processUuid) {
  const res = await axiosInstance.delete(API_ENDPOINTS.deleteProcess(processUuid));
  return res.data;
}

/** @param {string} cycleUuid */
export async function deleteSoxCycle(cycleUuid) {
  const res = await axiosInstance.delete(API_ENDPOINTS.deleteSoxCycle(cycleUuid));
  return res.data;
}
